import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { toast } from "react-toastify";

import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import {
  combinarOpcionesSede,
  expandirSedesSeleccionadas,
} from "../../utils/reporteExcelServicios.util";

const ModalReporteServicios = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("pdf");
  const [clientes, setClientes] = useState([]);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  // 🧩 Clientes (compartido por ambas tabs)
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const res = await axios.get(`${API}/clientes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClientes(res.data);
      } catch {
        toast.error("Error al cargar clientes");
      }
    };
    fetchClientes();
  }, [API, token]);

  /* =========================================================
     TAB 1: Reporte PDF individual (sin cambios de comportamiento)
     ========================================================= */
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);

  const [clienteId, setClienteId] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 🧩 Sedes
  useEffect(() => {
    if (!clienteId) return;
    const fetchSedes = async () => {
      try {
        const res = await axios.get(`${API}/sedes/${clienteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSedes([{ _id: "all", name: "Todas las sedes" }, ...res.data]);
      } catch {
        toast.error("Error al cargar sedes");
      }
    };
    fetchSedes();
  }, [clienteId, API, token]);

  // 🧩 Áreas
  useEffect(() => {
    if (!clienteId) return;
    const fetchAreas = async () => {
      try {
        const url = `${API}/areas/con_equipos?cliente=${clienteId}&sede=${
          sedeId || "all"
        }`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAreas([{ _id: "all", name: "Todas las áreas" }, ...res.data]);
      } catch {
        toast.error("Error al cargar áreas");
      }
    };
    fetchAreas();
  }, [clienteId, sedeId, API, token]);

  // 🔑 Handler genérico compatible con SelectField
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "cliente") {
      setClienteId(value);
      setSedeId("");
      setAreaId("");
      return;
    }

    if (name === "sede") {
      setSedeId(value);
      setAreaId("");
      return;
    }

    if (name === "area") setAreaId(value);
    if (name === "fechaInicio") setFechaInicio(value);
    if (name === "fechaFin") setFechaFin(value);
  };

  // 📄 Generar reporte PDF
  const handleGenerarReporte = async () => {
    if (!clienteId) {
      toast.warning("Seleccione un cliente para continuar");
      return;
    }
    if (!fechaInicio || !fechaFin) {
      toast.warning("Seleccione el rango de fechas");
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("clienteId", clienteId);
      if (sedeId && sedeId !== "all") params.append("sedeId", sedeId);
      if (areaId && areaId !== "all") params.append("areaId", areaId);
      params.append("fechaInicio", fechaInicio);
      params.append("fechaFin", fechaFin);

      const res = await axios.get(
        `${API}/reportes/grupal?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );

      const contentType = res.headers["content-type"];

      if (contentType.includes("application/pdf")) {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "reporte_servicios.pdf";
        link.click();
        toast.success("Reporte de servicios generado ✅");
        onClose();
      } else {
        const text = await res.data.text();
        const json = JSON.parse(text);
        if (json.success === false) toast.info(json.message);
        else toast.error("No se pudo generar el reporte");
      }
    } catch (err) {
      toast.error("Error al generar el reporte");
    } finally {
      setIsLoading(false);
    }
  };

  /* =========================================================
     TAB 2: Reporte Excel general (nuevo)
     ========================================================= */
  const [excelClienteIds, setExcelClienteIds] = useState([]);
  const [sedeGrupos, setSedeGrupos] = useState([]);
  const [excelSedesIndividuales, setExcelSedesIndividuales] = useState([]);
  const [excelSedeSeleccionIds, setExcelSedeSeleccionIds] = useState([]);
  const [excelAreas, setExcelAreas] = useState([]);
  const [excelAreaIds, setExcelAreaIds] = useState([]);
  const [excelFechaInicio, setExcelFechaInicio] = useState("");
  const [excelFechaFin, setExcelFechaFin] = useState("");
  const [excelLoading, setExcelLoading] = useState(false);

  // 🧩 Grupos de sedes (empresa completa, se cargan una sola vez)
  useEffect(() => {
    const fetchSedeGrupos = async () => {
      try {
        const res = await axios.get(`${API}/sede-grupos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSedeGrupos(res.data);
      } catch {
        toast.error("Error al cargar grupos de sedes");
      }
    };
    fetchSedeGrupos();
  }, [API, token]);

  // 🧩 Sedes individuales (unión de las sedes de cada cliente seleccionado)
  useEffect(() => {
    if (!excelClienteIds.length) {
      setExcelSedesIndividuales([]);
      return;
    }
    const fetchSedesExcel = async () => {
      try {
        const resultados = await Promise.all(
          excelClienteIds.map((clienteId) =>
            axios.get(`${API}/sedes/${clienteId}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ),
        );
        const todas = resultados.flatMap((r) => r.data);
        const unicas = Array.from(
          new Map(todas.map((s) => [s._id, s])).values(),
        );
        setExcelSedesIndividuales(unicas);
      } catch {
        toast.error("Error al cargar sedes");
      }
    };
    fetchSedesExcel();
  }, [excelClienteIds, API, token]);

  const sedeOpcionesCombinadas = useMemo(
    () => combinarOpcionesSede(sedeGrupos, excelSedesIndividuales),
    [sedeGrupos, excelSedesIndividuales],
  );

  const excelSedeIdsExpandido = useMemo(
    () =>
      expandirSedesSeleccionadas(excelSedeSeleccionIds, sedeOpcionesCombinadas),
    [excelSedeSeleccionIds, sedeOpcionesCombinadas],
  );

  // 🧩 Áreas (unión por cada combinación cliente × sede resuelta)
  useEffect(() => {
    if (!excelClienteIds.length) {
      setExcelAreas([]);
      return;
    }
    const fetchAreasExcel = async () => {
      try {
        const sedesParaConsultar = excelSedeIdsExpandido.length
          ? excelSedeIdsExpandido
          : ["all"];

        const combinaciones = excelClienteIds.flatMap((clienteId) =>
          sedesParaConsultar.map((sedeId) => ({ clienteId, sedeId })),
        );

        const resultados = await Promise.all(
          combinaciones.map(({ clienteId, sedeId }) =>
            axios.get(
              `${API}/areas/con_equipos?cliente=${clienteId}&sede=${sedeId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          ),
        );
        const todas = resultados.flatMap((r) => r.data);
        const unicas = Array.from(
          new Map(todas.map((a) => [a._id, a])).values(),
        );
        setExcelAreas(unicas);
      } catch {
        toast.error("Error al cargar áreas");
      }
    };
    fetchAreasExcel();
  }, [excelClienteIds, excelSedeIdsExpandido, API, token]);

  // Descartar áreas seleccionadas que ya no existen en la lista actual
  useEffect(() => {
    setExcelAreaIds((prev) =>
      prev.filter((id) => excelAreas.some((a) => a._id === id)),
    );
  }, [excelAreas]);

  // 📄 Generar reporte Excel
  const handleGenerarReporteExcel = async () => {
    if (!excelFechaInicio || !excelFechaFin) {
      toast.warning("Seleccione el rango de fechas");
      return;
    }

    setExcelLoading(true);
    try {
      const params = new URLSearchParams();
      if (excelClienteIds.length)
        params.append("clienteIds", excelClienteIds.join(","));
      if (excelSedeIdsExpandido.length)
        params.append("sedeIds", excelSedeIdsExpandido.join(","));
      if (excelAreaIds.length)
        params.append("areaIds", excelAreaIds.join(","));
      params.append("fechaInicio", excelFechaInicio);
      params.append("fechaFin", excelFechaFin);

      const res = await axios.get(
        `${API}/reportes/servicios-excel?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );

      const contentType = res.headers["content-type"];

      if (contentType.includes("spreadsheetml")) {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `reporte_mantenimientos_${excelFechaInicio}_${excelFechaFin}.xlsx`;
        link.click();
        toast.success("Reporte Excel generado ✅");
        onClose();
      } else {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || "No se pudo generar el reporte");
      }
    } catch (err) {
      toast.error("Error al generar el reporte Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const TABS = [
    { key: "pdf", label: "Reporte PDF" },
    { key: "excel", label: "Reporte Excel general" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Generar Reporte de Servicios
          </h2>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 overflow-x-auto">
          <div className="flex gap-1 min-w-max pb-2">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scroll interno */}
        <div className="flex-1 overflow-y-auto border-t">
          {activeTab === "pdf" && (
            <div className="p-6 space-y-4">
              <SelectField
                label="Cliente"
                name="cliente"
                value={clienteId}
                options={clientes}
                onChange={handleChange}
                displayFn={(c) => `[${c.nit}] ${c.name}`}
              />

              {clienteId && (
                <SelectField
                  label="Sede"
                  name="sede"
                  value={sedeId}
                  options={sedes}
                  onChange={handleChange}
                />
              )}

              {sedeId && (
                <SelectField
                  label="Área"
                  name="area"
                  value={areaId}
                  options={areas}
                  onChange={handleChange}
                  displayFn={(a) =>
                    a.grupo ? `${a.grupo.name} - ${a.name}` : a.name
                  }
                />
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Fecha inicio"
                  type="date"
                  name="fechaInicio"
                  value={fechaInicio}
                  onChange={handleChange}
                />

                <InputField
                  label="Fecha fin"
                  type="date"
                  name="fechaFin"
                  value={fechaFin}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {activeTab === "excel" && (
            <div className="p-6 space-y-4">
              <SelectField
                label="Cliente"
                name="excelCliente"
                value={excelClienteIds}
                options={clientes}
                isMulti
                placeholder="Todos los clientes"
                displayFn={(c) => `[${c.nit}] ${c.name}`}
                onChange={(e) =>
                  setExcelClienteIds(e.target.value.map((c) => c._id))
                }
              />

              <SelectField
                label="Sede / grupo de sedes"
                name="excelSede"
                value={excelSedeSeleccionIds}
                options={sedeOpcionesCombinadas}
                isMulti
                placeholder="Todas las sedes"
                onChange={(e) =>
                  setExcelSedeSeleccionIds(e.target.value.map((s) => s._id))
                }
              />

              <SelectField
                label="Área"
                name="excelArea"
                value={excelAreaIds}
                options={excelAreas}
                isMulti
                isDisabled={!excelClienteIds.length}
                placeholder={
                  excelClienteIds.length
                    ? "Todas las áreas"
                    : "Seleccione un cliente primero"
                }
                displayFn={(a) =>
                  a.grupo ? `${a.grupo.name} - ${a.name}` : a.name
                }
                onChange={(e) =>
                  setExcelAreaIds(e.target.value.map((a) => a._id))
                }
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Fecha inicio"
                  type="date"
                  name="excelFechaInicio"
                  value={excelFechaInicio}
                  onChange={(e) => setExcelFechaInicio(e.target.value)}
                />

                <InputField
                  label="Fecha fin"
                  type="date"
                  name="excelFechaFin"
                  value={excelFechaFin}
                  onChange={(e) => setExcelFechaFin(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          {activeTab === "pdf" ? (
            <Button
              onClick={handleGenerarReporte}
              disabled={isLoading}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {isLoading ? "Generando..." : "Generar Reporte"}
            </Button>
          ) : (
            <Button
              onClick={handleGenerarReporteExcel}
              disabled={excelLoading}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {excelLoading ? "Generando..." : "Generar Excel"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalReporteServicios;
